/**
 * Isometries of the Poincaré disk.
 *
 * Every isometry is z ↦ (a·w + b) / (b̄·w + ā) with w = z (orientation preserving) or w = z̄
 * (orientation reversing, `flip`), normalised so that |a|² − |b|² = 1. The matrix
 * [[a, b], [b̄, ā]] is an element of SU(1,1); composition is matrix multiplication with a
 * complex-conjugation twist when the outer map flips orientation.
 */
import { type Complex, abs2, conj, cx } from './complex';

export interface Isometry {
  a: Complex;
  b: Complex;
  flip: boolean;
}

export const IDENTITY: Isometry = Object.freeze({ a: cx(1), b: cx(0), flip: false });

export function apply(g: Isometry, z: Complex): Complex {
  const wr = z.re;
  const wi = g.flip ? -z.im : z.im;
  const { a, b } = g;
  // numerator a·w + b, denominator b̄·w + ā
  const nr = a.re * wr - a.im * wi + b.re;
  const ni = a.re * wi + a.im * wr + b.im;
  const dr = b.re * wr + b.im * wi + a.re;
  const di = b.re * wi - b.im * wr - a.im;
  const d = dr * dr + di * di;
  return { re: (nr * dr + ni * di) / d, im: (ni * dr - nr * di) / d };
}

/** Matrix product of the SU(1,1) forms [[a1,b1],[b̄1,ā1]]·[[a2,b2],[b̄2,ā2]]. */
function matMul(a1: Complex, b1: Complex, a2: Complex, b2: Complex): { a: Complex; b: Complex } {
  return {
    // a = a1·a2 + b1·b̄2
    a: {
      re: a1.re * a2.re - a1.im * a2.im + b1.re * b2.re + b1.im * b2.im,
      im: a1.re * a2.im + a1.im * a2.re + b1.im * b2.re - b1.re * b2.im,
    },
    // b = a1·b2 + b1·ā2
    b: {
      re: a1.re * b2.re - a1.im * b2.im + b1.re * a2.re + b1.im * a2.im,
      im: a1.re * b2.im + a1.im * b2.re + b1.im * a2.re - b1.re * a2.im,
    },
  };
}

/** g ∘ h, i.e. z ↦ g(h(z)). */
export function compose(g: Isometry, h: Isometry): Isometry {
  const a2 = g.flip ? conj(h.a) : h.a;
  const b2 = g.flip ? conj(h.b) : h.b;
  const m = matMul(g.a, g.b, a2, b2);
  return { a: m.a, b: m.b, flip: g.flip !== h.flip };
}

export function inverse(g: Isometry): Isometry {
  if (!g.flip) return { a: conj(g.a), b: { re: -g.b.re, im: -g.b.im }, flip: false };
  // g(z) = M(z̄)  ⇒  g⁻¹(w) = conj(M⁻¹(w)) = conj(M⁻¹)(w̄)
  return { a: { ...g.a }, b: { re: -g.b.re, im: g.b.im }, flip: true };
}

/** Rescales to |a|² − |b|² = 1 (removes floating-point drift after many compositions). */
export function normalize(g: Isometry): Isometry {
  const det = abs2(g.a) - abs2(g.b);
  if (!(det > 0)) throw new Error('Not a disk isometry (|a|² − |b|² ≤ 0)');
  const s = 1 / Math.sqrt(det);
  return {
    a: { re: g.a.re * s, im: g.a.im * s },
    b: { re: g.b.re * s, im: g.b.im * s },
    flip: g.flip,
  };
}

/** Rotation about the origin by θ. */
export function rotation(theta: number): Isometry {
  return { a: { re: Math.cos(theta / 2), im: Math.sin(theta / 2) }, b: cx(0), flip: false };
}

/** The hyperbolic translation along the diameter through p that maps 0 to p (|p| < 1). */
export function translation(p: Complex): Isometry {
  const s = 1 / Math.sqrt(1 - abs2(p));
  return { a: cx(s), b: { re: p.re * s, im: p.im * s }, flip: false };
}

/** The pure translation along the geodesic through p and q that maps p to q. */
export function translateBetween(p: Complex, q: Complex): Isometry {
  const tp = translation(p);
  const w = apply(inverse(tp), q);
  return normalize(compose(tp, compose(translation(w), inverse(tp))));
}

/** Rotation by θ about the point c. */
export function rotationAbout(c: Complex, theta: number): Isometry {
  const tc = translation(c);
  return normalize(compose(tc, compose(rotation(theta), inverse(tc))));
}

/** Reflection in the real axis, z ↦ z̄. */
export const REFLECT_REAL: Isometry = Object.freeze({ a: cx(1), b: cx(0), flip: true });

/** Reflection in the diameter at angle φ: z ↦ e^{2iφ} z̄. */
export function reflectLine(phi: number): Isometry {
  return { a: { re: Math.cos(phi), im: Math.sin(phi) }, b: cx(0), flip: true };
}

/**
 * Reflection (inversion) in the geodesic carried by the circle |z − c| = ρ, which is orthogonal
 * to the unit circle when |c|² = 1 + ρ². z ↦ c + ρ²/(z̄ − c̄) = (c·z̄ − 1)/(z̄ − c̄).
 */
export function reflectCircle(c: Complex, rho: number): Isometry {
  return { a: { re: -c.im / rho, im: c.re / rho }, b: { re: 0, im: -1 / rho }, flip: true };
}

/** True when g and h agree (as maps) to within tol, tested on a few probe points. */
export function sameIsometry(g: Isometry, h: Isometry, tol = 1e-9): boolean {
  if (g.flip !== h.flip) return false;
  for (const z of PROBES) {
    const u = apply(g, z);
    const v = apply(h, z);
    if (Math.abs(u.re - v.re) > tol || Math.abs(u.im - v.im) > tol) return false;
  }
  return true;
}
const PROBES: Complex[] = [cx(0), cx(0.31, 0.12), cx(-0.2, 0.37)];

/* ------------------------------------------------------------------------------------------ *
 * The Lie algebra su(1,1): one-parameter groups of motions (used for inertia, drift, keys).
 * A generator is X = [[iα, β], [β̄, −iα]]; X² = (|β|² − α²)·I, so exp(tX) has a closed form.
 * ------------------------------------------------------------------------------------------ */

export interface Generator {
  alpha: number;
  beta: Complex;
}

export function expGenerator(X: Generator, t = 1): Isometry {
  const al = X.alpha * t;
  const br = X.beta.re * t;
  const bi = X.beta.im * t;
  const delta = br * br + bi * bi - al * al;
  let c: number;
  let s: number; // sinh(√δ)/√δ (or sin variant)
  if (delta > 1e-12) {
    const r = Math.sqrt(delta);
    c = Math.cosh(r);
    s = Math.sinh(r) / r;
  } else if (delta < -1e-12) {
    const r = Math.sqrt(-delta);
    c = Math.cos(r);
    s = Math.sin(r) / r;
  } else {
    c = 1 + delta / 2;
    s = 1 + delta / 6;
  }
  return normalize({ a: { re: c, im: s * al }, b: { re: s * br, im: s * bi }, flip: false });
}

/**
 * Logarithm of an orientation-preserving isometry close enough to the identity (rotation part
 * below π). Returns X with expGenerator(X) = g.
 */
export function logIsometry(g: Isometry): Generator {
  if (g.flip) throw new Error('logIsometry needs an orientation-preserving isometry');
  // SU(1,1) double-covers the isometry group: pick the sign with Re(a) ≥ 0.
  const sgn = g.a.re < 0 ? -1 : 1;
  const ar = g.a.re * sgn;
  const ai = g.a.im * sgn;
  const br = g.b.re * sgn;
  const bi = g.b.im * sgn;
  let k: number;
  if (ar > 1 + 1e-12) {
    const phi = Math.acosh(ar);
    k = phi / Math.sinh(phi);
  } else if (ar < 1 - 1e-12) {
    const phi = Math.acos(Math.min(1, ar));
    k = phi / Math.sin(phi);
  } else {
    k = 1;
  }
  return { alpha: ai * k, beta: { re: br * k, im: bi * k } };
}

export const scaleGenerator = (X: Generator, s: number): Generator => ({
  alpha: X.alpha * s,
  beta: { re: X.beta.re * s, im: X.beta.im * s },
});

export const addGenerators = (X: Generator, Y: Generator): Generator => ({
  alpha: X.alpha + Y.alpha,
  beta: { re: X.beta.re + Y.beta.re, im: X.beta.im + Y.beta.im },
});

/** Size of a generator: hyperbolic speed at the origin plus angular speed. */
export const generatorNorm = (X: Generator): number =>
  2 * Math.hypot(X.beta.re, X.beta.im) + 2 * Math.abs(X.alpha);

/**
 * Translation through the origin in direction `dir` (a complex number, need not be unit) at
 * hyperbolic speed `speed` (distance per unit time measured at the origin).
 */
export function translationGenerator(dir: Complex, speed: number): Generator {
  const n = Math.hypot(dir.re, dir.im) || 1;
  const s = speed / 2 / n; // exp(tX) moves 0 to tanh(t|β|)·dir ⇒ distance 2t|β|
  return { alpha: 0, beta: { re: dir.re * s, im: dir.im * s } };
}

/** Rotation about the origin at angular speed ω (radians per unit time). */
export const rotationGenerator = (omega: number): Generator => ({ alpha: omega / 2, beta: cx(0) });

/**
 * Parabolic flow fixing the ideal point ζ = e^{iφ}: points slide along horocycles tangent to the
 * boundary at ζ. In the upper half-plane this is w ↦ w + t; conjugated into the disk.
 */
export function parabolicGenerator(phi: number, speed: number): Generator {
  // Cayley C(z) = i(1+z)/(1−z) sends z = 1 to ∞; d/dt of C⁻¹∘(w + t)∘C at t = 0 is
  // X = ½·[[i, −i], [i, −i]]: in our normal form α = ½, β = −i/2.
  // Rotating so the fixed point sits at e^{iφ}: X' = R X R⁻¹ with R = rotation(φ).
  const half = speed / 2;
  const beta = { re: Math.sin(phi) * half, im: -Math.cos(phi) * half };
  return { alpha: half, beta };
}
